/**
 * @React
 */
import {
    FC,
    useEffect,
    useState
} from "react";
/**
 * @Router
 */
import { useParams } from 'react-router-dom';
/**
 * @Yup
 */
import * as yup from "yup";
/**
 * @MUI
 */
import {
    CardContent,
    Card,
    Alert,
    Box,
    Button,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField
} from "@mui/material";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
/**
 * @Context
 */
import {
    useGeneralInfoContext,
} from "context";
import { useAuth } from "context/AuthContext";
import useAccess from "hooks/useAccess";

/**
 * @Components
 */
import Modal from "components/CustomComponents/Modal";
import Loader from "components/Loader";
/**
 * @Types
 */
import {
    IMonitoringAgreementCgsModel,
    monitoringStatus,
} from "types";
/**
 * @Utilities
 */
import { generateUrl } from "utilities/generateRequestUrl";
import config from "utilities/generateRequestUrl/config";
import { useFetchData } from "hooks";
import MonitoringCgsDetails from "components/detailedApplication/MonitoringApplication/MonitoringCgsDetails";
import { IMonitoringCgsFields } from "types/monitoringCgsFields";
import MessageBox from "components/CustomComponents/MessageBox";
import { monitoringAgreementCgsDetailsFields, monitoringCgsDetailsFields } from "utilities/Fields/CgsMonitoringFields/fields/detailFields";
import CardsLayout from "components/CustomComponents/CardsLayout";
import axiosInstance from "utilities/axios";
import { isDeadline, isMoreThanMonth} from "utilities/dates";
import { monitoringCgsInitialDataSchemaSubmit } from "utilities/Fields/CgsMonitoringFields/fields/forms/validation";
import CardFieldSet from "components/CustomComponents/CardFieldSet";
import ReassignDialog from "components/dialogs/ReassignDialog";
import { statusRules, StatusCtx } from "./statusRules";

const MonitoringCgsApplicantDetailPage: FC = () => {
    const { id } = useParams<{ id: string }>();
    const [approvingLoader, setApprovingLoader] = useState<boolean>(false);
    const [isOpen, setOpen] = useState<boolean>(false);
    const [isDeadlineComment, setIsDeadlineComment] = useState<boolean>(false);
    const [approveStatus, setApproveStatus] = useState<string>("");
    const [deadlineComment, setDeadlineComment] = useState<string>("");
    const [rejectReassignOpen, setRejectReassignOpen] = useState<boolean>(false);
    const [isInAgencyScope, setIsInAgencyScope] = useState<boolean>(false);
    const [agencyApproveComment, setAgencyApproveComment] = useState<string>("");

    const { data, load: loading, error: detailsError, fetchData } = useFetchData<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel> | null>(generateUrl(config.client, 'getApplicantCgs', { id: id }, null));

    const { isMonitoring, isCgs, isAgency, isHead, canReviewApp } = useAccess();
    const { user: currentUser } = useAuth();

    useEffect(() => {
        if (isAgency && data?.monitoring_assignment) {
            axiosInstance
                .get(generateUrl(config.client, "getAssignments", null, { is_active: "true", application_type: "cgs" }))
                .then((res) => {
                    const inScope = (res.data || []).some(
                        (a: any) => String(a._id) === String((data.monitoring_assignment as any)?._id ?? data.monitoring_assignment) && a.organization?.org_type === "agency"
                    );
                    setIsInAgencyScope(inScope);
                })
                .catch(() => {});
        }
    }, [isAgency, data?.monitoring_assignment, currentUser?.organization?._id]);

    let {
        generatedValues,
    } = useGeneralInfoContext();

    useEffect(() => {
        if (data && data?.monitoring_approval_deadline_comment) {
            setDeadlineComment(data?.monitoring_approval_deadline_comment || "");
        }
    }, [data, data?.monitoring_approval_deadline_comment])



    const submitFn = async (data: {
        _id: string;
        unfulfilled_used_amount_month_day?: number;
        monitoring_new_deadline?: Date | null;
        monitoring_deadline_comment?: string;
        is_deadline_approve?: boolean;
    }) => {
        try {
            setApprovingLoader(true);
            await axiosInstance.put(`/update-remonitoring-approve/${data._id}`, {
                application: {
                    unfulfilled_used_amount_month_day: data.unfulfilled_used_amount_month_day,
                    monitoring_new_deadline: data.monitoring_new_deadline,
                    monitoring_deadline_comment: data.monitoring_deadline_comment,
                    is_deadline_approve: data?.is_deadline_approve,
                }
            });
            if (fetchData) {
                fetchData();
            }
        } catch (error: Error | any) {

            if (error?.message) {
                alert(error?.message || 'დაფიქსირდა შეცდომა, გთხოვთ მიმართოთ ვებ-გვერდის ადმინისტრატორს');
                return;
            }
            alert('დაფიქსირდა შეცდომა, გთხოვთ მიმართოთ ვებ-გვერდის ადმინისტრატორს');
        } finally {
            setApprovingLoader(false);
        }
    };
    const handleApproveStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setApproveStatus(event.target.value);
    };
    const applicationApproval = async (data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel> | null, approval_status: boolean, approvalStatus: string, deadlineComment?: string) => {
        try {
            let url = "/monitoring/application_approval_cgs";
            setApprovingLoader(true);
            setOpen(false);
            setIsDeadlineComment(false);
            if (approvalStatus === "APPROVED") {
                await monitoringCgsInitialDataSchemaSubmit.validate(data, {
                    abortEarly: false,
                    context: {
                        machine_equipment_equipment: data?.machine_equipment_equipment,
                        vehicles_special_technic: data?.vehicles_special_technic,
                        purchase_building_land: data?.purchase_building_land,
                        building_repairs: data?.building_repairs,
                        funds_turnover: data?.funds_turnover,
                        repayment_amount: data?.repayment_amount,
                        is_sign_inscription_banner: data?.is_sign_inscription_banner,
                    },
                })
            }
            if (isAgency && approvalStatus === "APPROVED" && data?.is_sign_inscription_banner === true) {
                const signImages = data?.attached_documents?.sign_image;
                if (!Array.isArray(signImages) || signImages.length === 0) {
                    alert("გთხოვთ, ატვირთოთ აბრის ფოტო");
                    setApprovingLoader(false);
                    return;
                }
            }
            if (!approvalStatus) {
                throw new Error("დადასტურების სტატუსი არ არის შერჩეული")
            }
            let comment = ""
            if (!approval_status && ["REJECTED"].includes(approvalStatus)) {
                comment = prompt("კომენტარი") as string;
            }

            if (approvalStatus) {
                comment = `სტატუსი: ${monitoringStatus[approvalStatus as keyof typeof monitoringStatus]}; ${comment ? 'კომენტარი: ' + comment + ";" : ""}`;
            }
            if (isAgency && agencyApproveComment) {
                comment += ` კომენტარი: ${agencyApproveComment};`;
            }
            if (!window.confirm("არჩევანის დადასტურება")) {
                return;
            }
            if (
                isMonitoring && isCgs &&
                currentUser?.name === data?.created_by?.name &&
                data?.monitoring_approval_status === "UNDER_REVIEW" &&
                ["REJECTED"].includes(approvalStatus)
            ) {
                url = "/monitoring/application_reject_status_cgs";
            }
            if (isAgency && !canReviewApp && ["REMONITORING", "REJECTED"].includes(approvalStatus)) {
                url = "/monitoring/application_approval_agency_cgs";
            }

            await axiosInstance
                .post(url, {
                    application_id: id,
                    approval: approval_status,
                    comment,
                    approval_status_type: approvalStatus,
                    monitoring_approval_deadline_comment: deadlineComment ? deadlineComment : "",
                    agency_approve_comment: isAgency ? agencyApproveComment : undefined
                })
                .then((res) => {
                    alert(res.data.message);
                    window.location.reload();
                });
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const firstError = err.inner[0];
                const errorMessage = firstError.message;
                alert(`${errorMessage}`);
                return;
            }
            if (err?.response?.data?.message) {
                alert(err.response.data.message);
            } else {
                alert("დაფიქსირდა შეცდომა, გთხოვთ მიმართოთ ვებ-გვერდის ადმინისტრატორს")
            }
        } finally {
            setApprovingLoader(false);
        }
    }

    return (
        <>

            {
                loading
                    ?
                    <Loader />
                    :
                    detailsError ?
                        <MessageBox
                            alignHorizontal="center"
                        >
                            <Alert severity="error">
                                დაფიქსირდა შეცდომა, გთხოვთ, მიმართოთ საიტის ადმინისტრატორს
                            </Alert>
                        </MessageBox>

                        :
                        data
                            &&
                            generatedValues
                            ?
                            <CardsLayout>
                                <MonitoringCgsDetails
                                    data={data.contract as any || null}
                                    generatedValues={generatedValues}
                                    loading={loading}
                                    title={'სააგენტოში დადასტურების ეტაპზე არსებული დეტალური ინფორმაცია'}
                                    monitoringCgsDetailsFields={monitoringAgreementCgsDetailsFields}
                                />
                                <MonitoringCgsDetails
                                    data={data || null}
                                    generatedValues={generatedValues}
                                    loading={loading}
                                    title={'ჩატარებული მონიტორინგის დეტალური ინფორმაცია'}
                                    monitoringCgsDetailsFields={monitoringCgsDetailsFields}
                                    callback={submitFn}
                                />
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    justifyItems: "center",
                                    marginTop: "20px",
                                    gap: "50px"
                                }}>
                                    <>
                                        <Button
                                            disabled={
                                                approvingLoader || ["APPROVED", "REMOVAL"].includes(data?.monitoring_approval_status ?? "") ||
                                                (isAgency && !isInAgencyScope && ["REJECTED", "UNDER_REVIEW"].includes(data?.monitoring_approval_status ?? "")) ||
                                                (isMonitoring && !canReviewApp)
                                            }
                                            onClick={() => {
                                                setOpen(true);
                                            }}
                                            variant="contained"
                                            color="success"
                                            startIcon={<ThumbUpAltIcon />}
                                        >
                                            დადასტურება
                                        </Button>
                                        <Button
                                            disabled={approvingLoader || (isAgency && !isInAgencyScope && !canReviewApp)}
                                            onClick={() => isAgency ? setRejectReassignOpen(true) : applicationApproval(data, false, "REJECTED")}
                                            variant="contained"
                                            color="error"
                                            startIcon={<ThumbDownAltIcon />}
                                        >
                                            დახარვეზება
                                        </Button>
                                        {
                                            isOpen && !approvingLoader
                                                ?
                                                <Modal
                                                    open={isOpen}
                                                    handleClose={() => setOpen(false)}
                                                >
                                                    <Box sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        justifyItems: "center",
                                                        marginTop: "20px",
                                                        gap: "50px",
                                                        fontSize: "10px",
                                                    }}>
                                                        <RadioGroup
                                                            aria-labelledby="demo-radio-buttons-group-label"
                                                            defaultValue=""
                                                            name="radio-buttons-group"
                                                            value={approveStatus}
                                                            onChange={handleApproveStatusChange}
                                                            sx={{ "& .MuiTypography-root": { fontSize: "12px" } }}
                                                        >
                                                            {(() => {
                                                                const ctx: StatusCtx = { data, isMonitoring, isAgency, isCgs, canReviewApp, isInAgencyScope, isHead };
                                                                return statusRules
                                                                    .filter(rule => rule.when(ctx))
                                                                    .map(rule => (
                                                                        <FormControlLabel
                                                                            key={rule.value}
                                                                            value={rule.value}
                                                                            control={<Radio size="small" />}
                                                                            label={rule.label}
                                                                            disabled={rule.disabled(ctx)}
                                                                        />
                                                                    ));
                                                            })()}
                                                        </RadioGroup>
                                                        {isAgency && approveStatus && (
                                                            <TextField
                                                                label="კომენტარი"
                                                                multiline
                                                                rows={3}
                                                                value={agencyApproveComment}
                                                                onChange={(e) => setAgencyApproveComment(e.target.value)}
                                                                sx={{ width: "100%", fontSize: "12px" }}
                                                                InputProps={{ inputProps: { style: { fontSize: 12 } } }}
                                                            />
                                                        )}
                                                        <Box sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            justifyContent: "center",
                                                            justifyItems: "center",
                                                            marginTop: "20px",
                                                            gap: "10px",
                                                        }}>
                                                            <Button
                                                                disabled={!approveStatus}
                                                                onClick={() => {
                                                                    isDeadline(data?.monitoring_new_deadline || null, data?.monitoring_deadline || null) &&
                                                                        [
                                                                            "APPROVED",
                                                                            "REMOVAL",
                                                                        ].includes(approveStatus) ?
                                                                        setIsDeadlineComment(true) :
                                                                        applicationApproval(data, true, approveStatus, deadlineComment);
                                                                }}
                                                                variant="contained"
                                                                color="success"
                                                                sx={{ width: "150px", fontSize: "12px" }}
                                                            >
                                                                გაგზავნა
                                                            </Button>
                                                            <Button
                                                                onClick={() => {
                                                                    setOpen(false);
                                                                    setApproveStatus("");
                                                                    setAgencyApproveComment("");
                                                                    setDeadlineComment(data?.monitoring_approval_deadline_comment || "");
                                                                }}
                                                                variant="contained"
                                                                color="error"
                                                                sx={{ width: "150px", fontSize: "12px" }}
                                                            >
                                                                გაუქმება
                                                            </Button>
                                                        </Box>

                                                    </Box>

                                                </Modal> : null}
                                        {
                                            isDeadlineComment &&
                                                !approvingLoader
                                                ?
                                                <Modal
                                                    open={isDeadlineComment}
                                                    handleClose={() => setIsDeadlineComment(false)}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "10px",
                                                        }}
                                                    >
                                                        <label style={{
                                                            fontSize: "14px",
                                                            color: "rgb(0, 0, 0, 0.9)",
                                                        }}>
                                                            მონიტორინგის ვადაგადაცილები კომენტარი
                                                        </label>
                                                        <TextField
                                                            type={"text"}
                                                            label={""}
                                                            multiline
                                                            rows={6}
                                                            onChange={(e) => setDeadlineComment(e.target.value)}
                                                            value={deadlineComment}
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    "&:hover fieldset": {
                                                                        color: "#1976d2",
                                                                        borderColor: "#1976d2",
                                                                        borderWidth: "2px",
                                                                    },
                                                                },
                                                                width: '100%',
                                                            }}
                                                            InputLabelProps={{
                                                                sx: {
                                                                    fontSize: "1rem",
                                                                    top: '-2px',
                                                                },
                                                                shrink: true,
                                                            }}
                                                            InputProps={{
                                                                inputProps: {
                                                                    fontSize: 12,
                                                                    padding: '15px',
                                                                    transition: "font-size 0.2s, color 0.2s",
                                                                    style: {
                                                                        fontSize: 12,
                                                                    },
                                                                }
                                                            }}
                                                        />
                                                        <Box sx={{
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            justifyContent: "center",
                                                            justifyItems: "center",
                                                            marginTop: "20px",
                                                            gap: "10px",
                                                        }}>
                                                            <Button
                                                                disabled={!deadlineComment}
                                                                onClick={() => {
                                                                    applicationApproval(data, true, approveStatus, deadlineComment);
                                                                    setIsDeadlineComment(false);
                                                                }}
                                                                variant="contained"
                                                                color="success"
                                                                sx={{ width: "150px", fontSize: "12px" }}
                                                            >
                                                                გაგზავნა
                                                            </Button>
                                                            <Button
                                                                onClick={() => {
                                                                    setIsDeadlineComment(false);
                                                                    setDeadlineComment(data?.monitoring_approval_deadline_comment || "");
                                                                    setApproveStatus("");
                                                                }}
                                                                variant="contained"
                                                                color="error"
                                                                sx={{ width: "150px", fontSize: "12px" }}
                                                            >
                                                                გაუქმება
                                                            </Button>
                                                        </Box>

                                                    </Box>


                                                </Modal> : null
                                        }

                                    </>

                                </Box>
                            </CardsLayout >


                            :
                            <MessageBox
                                alignHorizontal="center"
                            >
                                <Alert severity="warning">
                                    განაცხადი არ მოიძებნა
                                </Alert>
                            </MessageBox>
            }
            <ReassignDialog
                open={rejectReassignOpen}
                onClose={() => setRejectReassignOpen(false)}
                onSuccess={() => { fetchData && fetchData(); }}
                application={data as any}
                approvalStatusType="REJECTED"
            />
        </>
    )
}

export default MonitoringCgsApplicantDetailPage;
